import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, MessageBox, Table, Typography } from '@dw/helix'
import { css } from '@emotion/core'
import { ParsedTrace } from '../../types/DataTypes'
import { infoRowStyles, noSearchResultsStyles, numberOfResultsStyles, panelStyles, placeRightStyles } from './styles'
import { truncateString } from '../../utils/truncate'
import pluralize from '../../utils/pluralize'

interface Props {
  traces: ParsedTrace[] | null
  error: boolean | undefined
  batchNumber: string
  canSeeLinkToBatch: boolean
  onTraceEventChange: (traceEvent: string) => void
}

const tableLinkStyles = css`
  border: none;
  width: 100%;
  align-self: stretch;
  background-color: transparent;
  text-align: left;
  padding: 0;
  min-height: 44px;
  display: flex;
  align-items: center;
`

const tableHandleOverflowStyles = css`
  width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const SerialResults: React.FunctionComponent<Props> = props => {
  const { batchNumber, canSeeLinkToBatch, onTraceEventChange } = props
  const [field, setField] = useState('traceTime')
  const [ascending, setAscending] = useState(true)

  const traces = props.traces || []
  const hasTraces = props.traces !== null

  const showReceivedBy = !!traces.filter(trace => trace.shipTo).length

  const columns = [
    {
      label: 'Trace Date',
      field: 'traceTime',
      width: '125px',
      sortable: true,
    },
    {
      label: 'Scanning Type',
      field: 'traceEvent',
      width: '127px',
      sortable: true,
    },
    {
      label: 'Order No.',
      field: 'orderNo',
      render: ({ orderNo }: ParsedTrace) => (
        <Button
          as={Link}
          to={`/trace/order/${orderNo}`}
          onClick={() => onTraceEventChange('order')}
          theme="tertiary"
          css={tableLinkStyles}
          title={orderNo}
        >
          {truncateString(orderNo, 12)}
        </Button>
      ),
      width: '150px',
      sortable: true,
    },
    {
      label: 'Article No.',
      field: 'productId',
      width: '136px',
      sortable: true,
    },
    {
      label: 'User Location',
      field: 'userLocation',
      width: '128px',
      sortable: true,
    },
    {
      label: 'Scanned By',
      field: 'userName',
      render: ({ userName }: ParsedTrace) => (
        <span title={userName} css={tableHandleOverflowStyles}>
          {userName}
        </span>
      ),
      width: '108px',
      sortable: true,
    },
    ...(showReceivedBy
      ? [
          {
            label: 'Received By',
            field: 'shipTo',
            render: ({ shipTo }: ParsedTrace) => (typeof shipTo === 'object' ? shipTo.Name : shipTo || ''),
            width: '108px',
          },
        ]
      : []),
  ]

  const sortedTraces = traces.sort((a, b) => {
    // @ts-ignore
    if (a[field] > b[field]) return ascending ? 1 : -1

    // @ts-ignore
    if (a[field] < b[field]) return ascending ? -1 : 1

    return 0
  })

  const onSortClick = (newField: string, newAscending: boolean) => {
    setField(newField)
    setAscending(newAscending)
  }
  return (
    <section className="e2e-test-serial-results">
      <div css={numberOfResultsStyles}>
        <Typography type="h4">{pluralize('result', traces.length)}</Typography>
      </div>
      <div css={panelStyles}>
        <div css={infoRowStyles}>
          <Typography type="h4">{traces.length > 0 && `Serial no ${traces[0].serialNo}`}</Typography>

          {batchNumber && (
            <>
              {canSeeLinkToBatch && (
                <Button theme="tertiary" as={Link} to={`/batch-numbers/batch/${batchNumber}`} css={placeRightStyles}>
                  Batch Number: {batchNumber}
                </Button>
              )}
              {!canSeeLinkToBatch && (
                <Typography type="caption" css={placeRightStyles}>
                  Batch Number: {batchNumber}
                </Typography>
              )}
            </>
          )}
        </div>
        <Table
          sortable
          items={sortedTraces}
          columns={columns}
          rowKey="key"
          initialSortingField="traceTime"
          onSortClick={onSortClick}
        />

        {!props.error && !hasTraces && (
          <Typography css={noSearchResultsStyles} type="caption">
            No search results yet.
          </Typography>
        )}
        {props.error && (
          <MessageBox
            css={css`
              width: 100%;
              margin-top: 12px;
            `}
            type="error"
          >
            Something went wrong. If the issue persists, please contact your admin.
          </MessageBox>
        )}
        {hasTraces && traces.length === 0 && (
          <MessageBox
            css={css`
              width: 100%;
              margin-top: 12px;
            `}
            type="primary"
            className="e2e-test-no-results-message"
          >
            No results found.
          </MessageBox>
        )}
      </div>
    </section>
  )
}
